import React, { useState, useEffect } from "react";

import {
  useEditor,
  EditorContent,
  ReactRenderer,
} from "@tiptap/react";

import {
  BubbleMenu,
  FloatingMenu,
} from "@tiptap/react/menus";

import StarterKit from "@tiptap/starter-kit";

import Placeholder from "@tiptap/extension-placeholder";

import Link from "@tiptap/extension-link";

import Underline from "@tiptap/extension-underline";

import TextAlign from "@tiptap/extension-text-align";

import Highlight from "@tiptap/extension-highlight";

import Suggestion from "@tiptap/suggestion";

import { Extension } from "@tiptap/core";

import tippy from "tippy.js";

import "tippy.js/dist/tippy.css";

import "./Editor.css";

/* -------------------------------------------------------------------------- */
/*                               SLASH COMMANDS                               */
/* -------------------------------------------------------------------------- */

const commands = [
  {
    title: "Heading 1",
    command: ({ editor }) =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },

  {
    title: "Heading 2",
    command: ({ editor }) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },

  {
    title: "Heading 3",
    command: ({ editor }) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },

  {
    title: "Bullet List",
    command: ({ editor }) =>
      editor.chain().focus().toggleBulletList().run(),
  },

  {
    title: "Ordered List",
    command: ({ editor }) =>
      editor.chain().focus().toggleOrderedList().run(),
  },

  {
    title: "Code Block",
    command: ({ editor }) =>
      editor.chain().focus().toggleCodeBlock().run(),
  },
];

const CommandList = (props) => {
  return (
    <div className="slash-menu">
      {props.items.map((item, index) => (
        <button
          key={index}
          onClick={() => props.command(item)}
          className="slash-item"
        >
          {item.title}
        </button>
      ))}
    </div>
  );
};

const SlashCommand = Extension.create({
  name: "slash-command",

  addOptions() {
    return {
      suggestion: {
        char: "/",

        command: ({ editor, props }) => {
          props.command({ editor });
        },

        items: ({ query }) => {
          return commands.filter((item) =>
            item.title.toLowerCase().includes(query.toLowerCase())
          );
        },

        render: () => {
          let component;
          let popup;

          return {
            onStart: (props) => {
              component = new ReactRenderer(CommandList, {
                props,
                editor: props.editor,
              });

              popup = tippy("body", {
                getReferenceClientRect: props.clientRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
              });
            },

            onUpdate(props) {
              component.updateProps(props);

              popup[0].setProps({
                getReferenceClientRect: props.clientRect,
              });
            },

            onKeyDown(props) {
              if (props.event.key === "Escape") {
                popup[0].hide();
                return true;
              }

              return false;
            },

            onExit() {
              popup[0].destroy();
              component.destroy();
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});

/* -------------------------------------------------------------------------- */
/*                                   EDITOR                                   */
/* -------------------------------------------------------------------------- */

const Editor = ({
  formData,
  setFormData,
  field = "description",
}) => {

  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({

    extensions: [

      StarterKit.configure({
        link: false, 
        underline: false,
      }),

      Underline,

      Placeholder.configure({
        placeholder: 'Write something or type "/"...',
      }),

      Link.configure({
        openOnClick: false,
        autolink: false,
      }),

      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),

      Highlight.configure({
        multicolor: true,
      }),

      SlashCommand,
    ],

    content: formData[field] || "",

    onUpdate({ editor }) {

      setFormData((prev) => ({
        ...prev,
        [field]: editor.getHTML(),
      }));
    },
  });

  useEffect(() => {

    if (
      editor &&
      formData[field] !== editor.getHTML()
    ) {
      editor.commands.setContent(
        formData[field] || ""
      );
    }

  }, [formData, field, editor]);

  if (!editor) return null;

  return (

    <div className="editor-container">

      {/* BUBBLE MENU */}

      <BubbleMenu
          editor={editor}
          shouldShow={({ editor }) => {
            return editor.view.state.selection.content().size > 0;
          }}
          tippyOptions={{
            placement: "top",
            appendTo: () => document.body,
            maxWidth: "none",
            zIndex: 99999,
          }}
        >
        <div className="modern-bubble-menu">

          {/* HEADING */}

          <select
            className="menu-select"
            onChange={(e) => {

              const value = e.target.value;

              if (value === "p") {
                editor.chain().focus().setParagraph().run();
              }

              if (value === "h1") {
                editor.chain().focus()
                  .toggleHeading({ level: 1 }).run();
              }

              if (value === "h2") {
                editor.chain().focus()
                  .toggleHeading({ level: 2 }).run();
              }

              if (value === "h3") {
                editor.chain().focus()
                  .toggleHeading({ level: 3 }).run();
              }

              if (value === "h4") {
                editor.chain().focus()
                  .toggleHeading({ level: 4 }).run();
              }
            }}
          >
            <option value="p">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
          </select>

          <div className="menu-divider"></div>

          {/* BOLD */}

          <button
            type="button"
            className={editor.isActive("bold") ? "active" : ""}
            onClick={() =>
              editor.chain().focus().toggleBold().run()
            }
          >
            <b>B</b>
          </button>

          {/* ITALIC */}

          <button
            type="button"
            className={editor.isActive("italic") ? "active" : ""}
            onClick={() =>
              editor.chain().focus().toggleItalic().run()
            }
          >
            <i>I</i>
          </button>

          {/* UNDERLINE */}

          <button
            type="button"
            className={editor.isActive("underline") ? "active" : ""}
            onClick={() =>
              editor.chain().focus().toggleUnderline().run()
            }
          >
            <u>U</u>
          </button>

          {/* STRIKE */}

          <button
            type="button"
            className={editor.isActive("strike") ? "active" : ""}
            onClick={() =>
              editor.chain().focus().toggleStrike().run()
            }
          >
            <s>S</s>
          </button>

          {/* HIGHLIGHT (same-line ke beech ke words ko bold/colored karne ke liye) */}

          <button
            type="button"
            className={editor.isActive("highlight") ? "active" : ""}
            onClick={() =>
              editor.chain().focus()
                .toggleHighlight({ color: "#241e74" })
                .run()
            }
          >
            🖍
          </button>

          <div className="menu-divider"></div>

          {/* BULLET */}

          <button
            type="button"
            onClick={() =>
              editor.chain().focus().toggleBulletList().run()
            }
          >
            •
          </button>

          {/* ORDERED */}

          <button
            type="button"
            onClick={() =>
              editor.chain().focus().toggleOrderedList().run()
            }
          >
            1.
          </button>

          {/* CODE */}

          <button
            type="button"
            onClick={() =>
              editor.chain().focus().toggleCodeBlock().run()
            }
          >
            {"</>"}
          </button>

          <div className="menu-divider"></div>

          {/* LINK */}

          <div className="link-box">

            <button
              type="button"
              className="link-action"
              onClick={() => {

                if (!linkUrl) return;

                editor.chain()
                  .focus()
                  .extendMarkRange("link")
                  .setLink({
                    href: linkUrl,
                  })
                  .run();

                setLinkUrl("");
              }}
            >
              🔗
            </button>

            <input
              type="text"
              placeholder="Paste a link..."
              value={linkUrl}
              onChange={(e) =>
                setLinkUrl(e.target.value)
              }
              className="link-input"
            />

            <button
              type="button"
              className="link-action"
              onClick={() => {

                editor.chain()
                  .focus()
                  .unsetLink()
                  .run();

                setLinkUrl("");
              }}
            >
              🗑
            </button>

          </div>
        </div>
      </BubbleMenu>

      {/* FLOATING MENU */}

      <FloatingMenu editor={editor}>
        <div className="floating-menu">

          <button
            type="button"
            onClick={() =>
              editor.chain().focus()
                .toggleHeading({ level: 1 }).run()
            }
          >
            H1
          </button>

          <button
            type="button"
            onClick={() =>
              editor.chain().focus()
                .toggleBulletList().run()
            }
          >
            Bullet
          </button>

          <button
            type="button"
            onClick={() =>
              editor.chain().focus()
                .toggleCodeBlock().run()
            }
          >
            Code
          </button>

        </div>
      </FloatingMenu>

      {/* CONTENT */}

      <EditorContent editor={editor} />

    </div>
  );
};

export default Editor;
